import { useState } from 'react';
import styled from 'styled-components';
import type { KsPackageAct } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { fmtMoney } from '@shared/lib/smetaLabels';

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const ThRight = styled.th`
  text-align: right !important;
`;

const Inline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const InlineField = styled(Field)`
  margin-bottom: 0;
`;

const AddActForm = styled.form`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoActsMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

export interface KsPackageActsTabProps {
  acts: KsPackageAct[];
  contractLsrs: { id: string; code: string; name?: string }[];
  canWrite: boolean;
  frozen: boolean;
  busy: boolean;
  rendering: boolean;
  renderingAct: boolean;
  savingAct: boolean;
  onRenderAct: (actId: string, actNo: string, forms: string[]) => void;
  onAddAct: (act: { actNo: string; actWorkKind: string; actLsrId: string }) => Promise<void>;
  actError: string | null;
}

export function KsPackageActsTab({
  acts,
  contractLsrs,
  canWrite,
  frozen,
  busy,
  rendering,
  renderingAct,
  savingAct,
  onRenderAct,
  onAddAct,
  actError,
}: KsPackageActsTabProps) {
  const [actNo, setActNo] = useState('');
  const [actWorkKind, setActWorkKind] = useState('');
  const [actLsrId, setActLsrId] = useState('');

  const handleAddAct = async (e: React.FormEvent) => {
    e.preventDefault();
    await onAddAct({ actNo, actWorkKind, actLsrId });
    setActNo('');
    setActWorkKind('');
    setActLsrId('');
  };

  return (
    <>
      <TableTitle>
        Акты КС-2 <Badge $tone="neutral">{acts.length}</Badge>
      </TableTitle>
      {acts.length === 0 && (
        <NoActsMuted>
          Актов нет. Сборщик раскладывает строки по актам пакета и сам их не заводит — добавьте хотя бы
          один.
        </NoActsMuted>
      )}
      {acts.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Акт</th>
                <th>Период</th>
                <th>Вид затрат</th>
                <ThRight>Итого за период</ThRight>
                <th />
              </tr>
            </thead>
            <tbody>
              {acts.map((a) => (
                <tr key={a.id}>
                  <td>№ {a.act_no}</td>
                  <td>
                    {a.period_from} — {a.period_to}
                  </td>
                  <td>{a.work_kind ?? '—'}</td>
                  <td className="num">{fmtMoney(a.total_period)}</td>
                  <td>
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      disabled={rendering || renderingAct || !canWrite || frozen || busy}
                      title={
                        !canWrite
                          ? 'Только чтение'
                          : 'КС-2 акта и все его приложения одной редакцией'
                      }
                      onClick={() => onRenderAct(a.id, a.act_no, [])}
                    >
                      Комплект акта
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {!frozen && (
        <AddActForm onSubmit={handleAddAct}>
          <Inline>
            <InlineField>
              <Label>Номер акта</Label>
              <Input
                value={actNo}
                onChange={(e) => setActNo(e.target.value)}
                placeholder="35.1"
              />
            </InlineField>
            <InlineField>
              <Label>Вид затрат</Label>
              <Select
                value={actWorkKind}
                onChange={(e) => setActWorkKind(e.target.value)}
              >
                <option value="">— не указан —</option>
                <option value="smr">СМР</option>
                <option value="transport">перевозки</option>
                <option value="demolition">разборка</option>
                <option value="bns">буронабивные сваи</option>
                <option value="correction">корректировка</option>
                <option value="pnr">пусконаладка</option>
              </Select>
            </InlineField>
            {contractLsrs.length > 0 && (
              <InlineField>
                <Label>Локальная смета (мир B)</Label>
                <Select
                  value={actLsrId}
                  onChange={(e) => setActLsrId(e.target.value)}
                >
                  <option value="">— все / по шифру —</option>
                  {contractLsrs.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} {l.name ? `· ${l.name.slice(0, 40)}` : ''}
                    </option>
                  ))}
                </Select>
              </InlineField>
            )}
            <Button
              type="submit"
              $variant="secondary"
              $size="sm"
              disabled={savingAct || !canWrite}
              title={!canWrite ? 'Только чтение' : undefined}
            >
              {savingAct ? 'Сохраняю…' : 'Добавить акт'}
            </Button>
          </Inline>
          <Hint>Период и дата акта берутся из пакета.</Hint>
          {actError && <Err>{actError}</Err>}
        </AddActForm>
      )}
    </>
  );
}
